import { Core } from '@pdftron/webviewer';

/**
 * Redraw only the given pages: invalidate each page's render cache and re-render.
 *
 * Use for page-local mutations (a crop/polygon overlay change, a single-page
 * annotation change) so the cost does not scale with document size. Prefer this
 * over `redrawAll` unless the change genuinely affects every page.
 */
export function redrawPages(documentViewer: Core.DocumentViewer, pages: number[]): void {
  const uniquePages = [...new Set(pages)].filter((page) => Number.isInteger(page) && page > 0);
  if (uniquePages.length === 0) return;
  uniquePages.forEach((page) => documentViewer.refreshPage(page));
  // Arg-less updateView: only repaint currently-visible pages. Do NOT pass the
  // page list — updateView([pages]) force-renders those pages (even off-screen)
  // at the current zoom, which races the difference-focus zoom and triggers
  // WebViewer's "Document or zoom changed while rendering" abort. Off-screen
  // invalidated pages redraw fresh (at the right zoom) when scrolled into view.
  documentViewer.updateView();
}

/**
 * Redraw the entire document: invalidate every page's render cache and re-render.
 *
 * Use only for doc-wide changes (layer/separation/overprint toggles, highlight
 * recolor) where the affected page set is the whole document or unknowable.
 */
export function redrawAll(documentViewer: Core.DocumentViewer): void {
  documentViewer.refreshAll();
  documentViewer.updateView();
}

/**
 * Watches WebViewer's render activity so work can be held until rendering has stopped, keeping a
 * render already underway from finishing afterwards and overwriting the result.
 *
 * WebViewer emits begin/finishedRendering but exposes no "currently rendering" state, and renders
 * nest, so active renders are counted rather than tracked with a boolean.
 */
export function watchRendering(documentViewer: Core.DocumentViewer) {
  let activeRenders = 0;
  let waitingTask: (() => void) | null = null;

  const onBeginRendering = () => {
    activeRenders += 1;
  };

  const onFinishedRendering = () => {
    activeRenders = Math.max(0, activeRenders - 1);
    if (activeRenders > 0 || !waitingTask) return;
    const task = waitingTask;
    waitingTask = null;
    task();
  };

  documentViewer.addEventListener('beginRendering', onBeginRendering);
  documentViewer.addEventListener('finishedRendering', onFinishedRendering);

  return {
    /**
     * Runs `task` now if nothing is rendering, otherwise once the last active render finishes.
     * Only one task waits at a time; calling again replaces the waiting one.
     */
    whenStopped(task: () => void) {
      if (activeRenders === 0) {
        task();
      } else {
        waitingTask = task;
      }
    },
    /** Detaches the render listeners and drops any task still waiting. */
    stopWatching() {
      waitingTask = null;
      documentViewer.removeEventListener('beginRendering', onBeginRendering);
      documentViewer.removeEventListener('finishedRendering', onFinishedRendering);
    },
  };
}
